import { Card, Typography, Tag, Space, Button, Popconfirm, Modal, Select } from "antd";
import { 
  EyeOutlined, 
  ClockCircleOutlined, 
  UserOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  CloseCircleOutlined,
  CarOutlined
} from "@ant-design/icons";
import { useState } from "react";
import moment from "moment";
import { IOrder } from "../../../redux/@types";
import { decimal } from "../../../shared/utils/Helper";
import { ordersService, usersService, shippingService } from "../../../shared/services";
import { notify } from "../../../shared/utils/notify";

const { Text, Title } = Typography;

interface OrderCardProps {
  order: IOrder;
  onViewDetails: (order: IOrder) => void;
  showTimer?: boolean;
  onStatusChanged?: () => void;
  orderStatus?: Array<{
    id: number;
    name: string;
    description: string;
    status_code: string;
    color: string;
  }>;
}

export const OrderCard = ({ order, onViewDetails, showTimer = false, onStatusChanged, orderStatus = [] }: OrderCardProps) => {
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [domiciliarios, setDomiciliarios] = useState<any[]>([]);
  const [selectedDomiciliario, setSelectedDomiciliario] = useState<number | null>(null);
  const [assigningDomiciliario, setAssigningDomiciliario] = useState(false);

  const loadDomiciliarios = async () => {
    try {
      const res: any = await usersService.find({ query: { role: 'domiciliary', $limit: 100 } });
      const data = res?.data || res || [];
      setDomiciliarios(data);
    } catch (err) {
      console.error('Error loading domiciliarios:', err);
    }
  };

  const handleAssignDomiciliario = async () => {
    if (!selectedDomiciliario) {
      notify.error('Selecciona un domiciliario');
      return;
    }

    setAssigningDomiciliario(true);
    try {
      const shippingRes: any = await shippingService.find({
        query: { order_id: order.id, $limit: 1 }
      });

      const shippings = shippingRes?.data || shippingRes || [];
      
      const assignPromise = async () => {
        if (shippings.length > 0) {
          await shippingService.patch(shippings[0].id, {
            delivery_guy_user_id: selectedDomiciliario,
            shipping_status_id: 2,
          });
        } else {
          await shippingService.create({
            order_id: order.id,
            delivery_guy_user_id: selectedDomiciliario,
            shipping_status_id: 2,
            fulfillment_company_id: order.fulfillment_company_id,
            price: 0,
          });
        }
      };

      await notify.promise(assignPromise(), {
        loading: "Asignando domiciliario...",
        success: "Domiciliario asignado correctamente",
        error: (err: any) => err.message || "Error al asignar domiciliario"
      });

      setShowAssignModal(false);
      setSelectedDomiciliario(null);
      if (onStatusChanged) onStatusChanged();
    } catch (err: any) {
      // El error ya es reportado por el toast promise
    } finally {
      setAssigningDomiciliario(false);
    }
  };

  const openAssignModal = () => {
    loadDomiciliarios();
    setShowAssignModal(true);
  };

  // Buscar el ID del estado cancelado/rechazado por el restaurante
  const getCancelledStatusId = () => {
    // Primero buscar estado específico de "rechazado por restaurante"
    let cancelledStatus = orderStatus.find(
      (s) => s.name.toLowerCase().includes("rechazado por el restaurante") ||
             s.name.toLowerCase().includes("rechazado por restaurante") ||
             s.status_code === "rejected_by_restaurant"
    );
    
    // Si no existe, buscar estado genérico de cancelado/rechazado
    if (!cancelledStatus) {
      cancelledStatus = orderStatus.find(
        (s) => s.status_code === "canceled" || 
               s.status_code === "cancelled" || 
               s.status_code === "rejected" ||
               s.name.toLowerCase().includes("cancelado") ||
               s.name.toLowerCase().includes("rechazado")
      );
    }
    
    return cancelledStatus?.id || 5; // Fallback a 5 si no se encuentra
  };
  
  // Obtener el nombre del estado cancelado
  const getCancelledStatusName = () => {
    const statusId = getCancelledStatusId();
    const status = orderStatus.find((s) => s.id === statusId);
    return status?.name || "Rechazado";
  };

  const getTimeSinceCreated = () => {
    const now = moment();
    const created = moment(order.createdAt);
    const diffMinutes = now.diff(created, 'minutes');
    
    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    } else {
      const diffHours = Math.floor(diffMinutes / 60);
      return `${diffHours}h ${diffMinutes % 60}min`;
    }
  };

  const updateOrderStatus = async (newStatusId: number, statusName: string) => {
    setLoading(true);
    try {
      await notify.promise(
        ordersService.patch(order.id, {
          order_status_id: newStatusId,
        }),
        {
          loading: "Actualizando estado...",
          success: `Orden movida a: ${statusName}`,
          error: (err: any) => err.message || "Error al actualizar el estado"
        }
      );

      if (onStatusChanged) {
        onStatusChanged();
      }
    } catch (err: any) {
      // El error ya es reportado por el toast promise
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = () => {
    const currentStatusId = order.order_status_id;
    
    // Estado 1: Pendiente por pago → puede ir a En Preparación (2) o Rechazar (5)
    if (currentStatusId === 1) {
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Button 
            type="primary" 
            size="middle" 
            icon={<CheckCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(2, "En Preparación");
            }}
            loading={loading}
            block
            style={{ 
              backgroundColor: "#52c41a", 
              borderColor: "#52c41a",
              borderRadius: 20,
              height: 40,
              fontWeight: 600,
              fontSize: 14,
              boxShadow: "0 2px 8px rgba(82, 196, 26, 0.3)",
              transition: "all 0.3s ease"
            }}
          >
            Aceptar orden
          </Button>
          <Popconfirm
            title="¿Estás seguro de que deseas rechazar este pedido?"
            onConfirm={(e) => {
              e?.stopPropagation();
              updateOrderStatus(getCancelledStatusId(), getCancelledStatusName());
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Sí, rechazar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button 
              danger
              size="middle" 
              icon={<CloseCircleOutlined />}
              onClick={(e) => e.stopPropagation()}
              loading={loading}
              block
              style={{ 
                borderRadius: 20,
                height: 36,
                fontWeight: 500,
                fontSize: 13,
                transition: "all 0.3s ease"
              }}
            >
              Rechazar pedido
            </Button>
          </Popconfirm>
        </Space>
      );
    }
    
    // Estado 2: En Preparación → puede ir a Para Entregar (3) o Rechazar (5)
    if (currentStatusId === 2) {
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Button 
            type="primary" 
            size="middle" 
            icon={<RocketOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(3, "Para Entregar");
            }}
            loading={loading}
            block
            style={{ 
              backgroundColor: "#1890ff", 
              borderColor: "#1890ff",
              borderRadius: 20,
              height: 40,
              fontWeight: 600,
              fontSize: 14,
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
              transition: "all 0.3s ease"
            }}
          >
            Listo para entregar
          </Button>
          <Popconfirm
            title="¿Estás seguro de que deseas cancelar este pedido?"
            onConfirm={(e) => {
              e?.stopPropagation();
              updateOrderStatus(getCancelledStatusId(), getCancelledStatusName());
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Sí, cancelar"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button 
              danger
              size="middle" 
              icon={<CloseCircleOutlined />}
              onClick={(e) => e.stopPropagation()}
              loading={loading}
              block
              style={{ 
                borderRadius: 20,
                height: 36,
                fontWeight: 500,
                fontSize: 13,
                transition: "all 0.3s ease"
              }}
            >
              Cancelar pedido
            </Button>
          </Popconfirm>
        </Space>
      );
    }
    
    // Estado 3 o 4: Para Entregar → puede asignar domiciliario o marcar entregado
    if (currentStatusId === 3 || currentStatusId === 4) {
      const hasDeliveryGuy = order.shippings?.some(s => s.delivery_guy_user_id);
      
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {!hasDeliveryGuy && (
            <Button 
              type="primary" 
              size="middle" 
              icon={<CarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openAssignModal();
              }}
              block
              style={{ 
                backgroundColor: "#722ed1", 
                borderColor: "#722ed1",
                borderRadius: 20,
                height: 40,
                fontWeight: 600,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(114, 46, 209, 0.3)",
                transition: "all 0.3s ease"
              }}
            >
              Asignar domiciliario
            </Button>
          )}
          <Button 
            type="primary" 
            size="middle" 
            icon={<CheckCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(9, "Entregado");
            }}
            loading={loading}
            block
            style={{ 
              backgroundColor: "#52c41a", 
              borderColor: "#52c41a",
              borderRadius: 20,
              height: 40,
              fontWeight: 600,
              fontSize: 14,
              boxShadow: "0 2px 8px rgba(82, 196, 26, 0.3)",
              transition: "all 0.3s ease"
            }}
          >
            Marcar como entregado
          </Button>
        </Space>
      );
    }
    
    return null;
  };

  const getShippingAddress = () => {
    try {
      const address = JSON.parse(order.shipping_address_meta_data);
      return address;
    } catch {
      return null;
    }
  };

  const address = getShippingAddress();
  const fulfillmentCompany = order.fulfillment_company?.name || "Sin asignar";

  return (
    <Card
      style={{
        marginBottom: 12,
        borderRadius: 12,
        cursor: "pointer",
        transition: "all 0.3s",
        border: "1px solid #e8e8e8",
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
      }}
      hoverable
      onClick={() => onViewDetails(order)}
      bodyStyle={{ padding: 14 }}
    >
      {/* Header con ID y Tienda */}
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Title level={5} style={{ margin: 0, color: "#00a0e3", fontWeight: 600 }}>
            ID: {order.id}
          </Title>
          {showTimer && (
            <Tag 
              icon={<ClockCircleOutlined />} 
              color="orange"
              style={{ margin: 0, fontSize: 11 }}
            >
              {getTimeSinceCreated()}
            </Tag>
          )}
        </div>

        <Text type="secondary" style={{ fontSize: 11, fontWeight: 500 }}>
          TIENDA {order.fulfillment_company_id}
        </Text>

        {/* Usuario */}
        {order.user && (
          <Space size={4}>
            <UserOutlined style={{ color: "#8c8c8c" }} />
            <Text strong style={{ fontSize: 13 }}>
              {order.user.first_name} {order.user.last_name}
            </Text>
            {order.shippings && order.shippings.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                en camino
              </Tag>
            )}
          </Space>
        )}

        {/* Método de entrega */}
        {fulfillmentCompany && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EnvironmentOutlined style={{ color: "#52c41a" }} />
            <Text style={{ fontSize: 12 }}>
              {fulfillmentCompany}
            </Text>
          </div>
        )}

        {/* Dirección */}
        {address && address.address && (
          <Text 
            type="secondary" 
            style={{ fontSize: 12 }} 
            ellipsis={{ tooltip: address.address }}
          >
            {address.address}
          </Text>
        )}

        {/* Método de pago y Total */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px solid #f0f0f0"
        }}>
          <Tag color={order.payment_method === "online" ? "blue" : "green"}>
            {order.payment_method === "online" ? "Online" : "Contra entrega"}
          </Tag>
          <Text strong style={{ fontSize: 14 }}>
            <DollarOutlined /> {decimal(order.total_price)}
          </Text>
        </div>

        {/* Botones de acción */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {getActionButtons()}
          <Button 
            size="middle" 
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(order);
            }}
            block
            style={{
              borderRadius: 20,
              height: 36,
              fontWeight: 500,
              fontSize: 13,
              borderColor: "#d9d9d9",
              transition: "all 0.3s ease"
            }}
          >
            Ver detalle
          </Button>
        </div>

        {/* Código de entrega si existe */}
        {order.integration_id && (
          <div style={{ 
            backgroundColor: "#f5f5f5", 
            padding: "4px 8px", 
            borderRadius: 4,
            marginTop: 4 
          }}>
            <Text style={{ fontSize: 11 }}>
              Código entrega: <strong>{order.integration_id}</strong>
            </Text>
          </div>
        )}

        {/* Domiciliario asignado */}
        {order.shippings?.[0]?.delivery_guy_user && (
          <div style={{ 
            backgroundColor: "#f0f5ff", 
            padding: "6px 10px", 
            borderRadius: 6,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
            <CarOutlined style={{ color: "#722ed1" }} />
            <Text style={{ fontSize: 12, color: "#722ed1" }}>
              <strong>Domiciliario:</strong> {order.shippings[0].delivery_guy_user.first_name} {order.shippings[0].delivery_guy_user.last_name}
            </Text>
          </div>
        )}
      </Space>

      {/* Modal para asignar domiciliario */}
      <Modal
        title="Asignar Domiciliario"
        open={showAssignModal}
        onCancel={(e) => {
          e.stopPropagation();
          setShowAssignModal(false);
          setSelectedDomiciliario(null);
        }}
        onOk={(e) => {
          e.stopPropagation();
          handleAssignDomiciliario();
        }}
        confirmLoading={assigningDomiciliario}
        okText="Asignar"
        cancelText="Cancelar"
      >
        <div onClick={(e) => e.stopPropagation()}>
          <p style={{ marginBottom: 16 }}>
            Selecciona el domiciliario para el pedido <strong>#{order.id}</strong>
          </p>
          <Select
            placeholder="Seleccionar domiciliario"
            style={{ width: "100%" }}
            value={selectedDomiciliario}
            onChange={(value) => setSelectedDomiciliario(value)}
            options={domiciliarios.map((d) => ({
              value: d.id,
              label: `${d.first_name} ${d.last_name}`,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Modal>
    </Card>
  );
};

